import { BirdBowlData } from "../../types/bird/bird-bowl-data";
import { BowlType } from "../../types/enums/bowl-type";
import { LongBowlType } from "../../types/enums/long-bowl-type";

export const BIN_COUNT = 40;
export type InsightsPageMetric =
  | "bounceHeight"
  | "slowDown"
  | "deviation"
  | "swing";

export const humanReadableMetric: Record<InsightsPageMetric, string> = {
  bounceHeight: "Bounce Height",
  slowDown: "Slowdown",
  deviation: "Deviation",
  swing: "Swing",
};

export const GROUND_COLOR_PALETTE = [
  "rgba(255, 99, 132, 1)",
  "rgba(54, 162, 235, 1)",
  "rgba(255, 206, 86, 1)",
  "rgba(75, 192, 192, 1)",
  "rgba(153, 102, 255, 1)",
  "rgba(255, 159, 64, 1)",
  "rgba(199, 199, 199, 1)",
  "rgba(83, 102, 255, 1)",
  "rgba(255, 102, 255, 1)",
  "rgba(102, 255, 178, 1)",
  "rgba(160, 160, 255, 1)",
  "rgba(0, 200, 200, 1)",
  "rgba(200, 100, 100, 1)",
];

export const HISTOGRAM_COLOR_PALETTE = [
  "rgba(255, 99, 132, 0.5)",
  "rgba(54, 162, 235, 0.5)",
  "rgba(255, 206, 86, 0.5)",
  "rgba(75, 192, 192, 0.5)",
  "rgba(153, 102, 255, 0.5)",
  "rgba(255, 159, 64, 0.5)",
];

export const STD_DEV_FACTOR = 10;

export function getScatterOptions(selectedMetric: InsightsPageMetric) {
  return {
    plugins: {
      legend: {
        display: true,
      },
      dragData: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "",
        },
      },
      y: {
        title: {
          display: true,
          text: humanReadableMetric[selectedMetric],
        },
      },
    },
  };
}

export const SMALL_BAR_OPTIONS = {
  plugins: {
    legend: { display: true },
    dragData: false,
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      title: { display: true, text: "Range" },
      stacked: true,
    },
    y: {
      title: { display: true, text: "Frequency" },
      stacked: true,
    },
  },
};

export const AXIS_LIMITS = {
  "bounceHeight": {
    min: 0.01,
    max: 0.08
  },
  "slowDown": {
    min: 1.15,
    max: 1.45
  },
  "deviation": {
    min: 0,
    max: 10
  },
  "swing": {
    min: 0,
    max: 5
  }
}

export function computeMean(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function computeStandardDeviation(values: number[], mean: number) {
  if (!values.length) return 0;
  const squaredDiffs = values.map((v) => (v - mean) ** 2);
  const avgSquaredDiff =
    squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(avgSquaredDiff);
}

export interface BirdDataRange {
  bounceHeightMin: number;
  bounceHeightMax: number;
  slowDownMin: number;
  slowDownMax: number;
  deviationMin: number;
  deviationMax: number;
  swingMin: number;
  swingMax: number;
}

export function calculateRange(historicUpdates: BirdBowlData[]): BirdDataRange {
  const historicBounceHeights = historicUpdates.map((u) => u.bounceHeight);
  const historicSlowDowns = historicUpdates.map((u) => u.slowDown);
  const historicDeviations = historicUpdates.map((u) => Math.abs(u.deviation));
  const historicSwings = historicUpdates.map((u) => Math.abs(u.swing));

  const meanBounceHeight = computeMean(historicBounceHeights);
  const stdDevBounceHeight = computeStandardDeviation(
    historicBounceHeights,
    meanBounceHeight
  );
  const bounceHeightMin =
    meanBounceHeight - STD_DEV_FACTOR * stdDevBounceHeight;
  const bounceHeightMax =
    meanBounceHeight + STD_DEV_FACTOR * stdDevBounceHeight;

  const meanSlowDown = computeMean(historicSlowDowns);
  const stdDevSlowDown = computeStandardDeviation(
    historicSlowDowns,
    meanSlowDown
  );
  const slowDownMin = meanSlowDown - STD_DEV_FACTOR * stdDevSlowDown;
  const slowDownMax = meanSlowDown + STD_DEV_FACTOR * stdDevSlowDown;

  const meanDeviation = computeMean(historicDeviations);
  const stdDevDeviation = computeStandardDeviation(
    historicDeviations,
    meanDeviation
  );
  const deviationMin = meanDeviation - STD_DEV_FACTOR * stdDevDeviation;
  const deviationMax = meanDeviation + STD_DEV_FACTOR * stdDevDeviation;

  const meanSwing = computeMean(historicSwings);
  const stdDevSwing = computeStandardDeviation(historicSwings, meanSwing);
  const swingMin = meanSwing - STD_DEV_FACTOR * stdDevSwing;
  const swingMax = meanSwing + STD_DEV_FACTOR * stdDevSwing;

  return {
    bounceHeightMin,
    bounceHeightMax,
    slowDownMin,
    slowDownMax,
    deviationMin,
    deviationMax,
    swingMin,
    swingMax,
  };
}

export function filterByMetricRange(
  update: BirdBowlData,
  range: BirdDataRange
): boolean {
  return (
    update.bouncePosition.x >= -5 &&
    update.bouncePosition.x <= 20 &&
    update.bouncePosition.y >= -1 &&
    update.bouncePosition.y <= 1 &&
    update.bounceHeight >= range.bounceHeightMin &&
    update.bounceHeight <= range.bounceHeightMax &&
    update.slowDown >= range.slowDownMin &&
    update.slowDown <= range.slowDownMax &&
    Math.abs(update.deviation) >= range.deviationMin &&
    Math.abs(update.deviation) <= range.deviationMax &&
    Math.abs(update.swing) >= range.swingMin &&
    Math.abs(update.swing) <= range.swingMax
  );
}

export function filterBySeam(
  update: BirdBowlData
): boolean {
  return (
    update.bowlType === BowlType.LEFT_ARM_PACE || update.bowlType === BowlType.RIGHT_ARM_PACE
  )
}

export function filterByAllSpin(
  update: BirdBowlData
): boolean {
  return (
    update.bowlType === BowlType.LEFT_ARM_SPIN || update.bowlType === BowlType.RIGHT_ARM_SPIN
  )
}

export function filterByWristSpin(
  update: BirdBowlData
): boolean {
  const WRIST_SPIN_LONG_TYPES = [LongBowlType.LEGBREAK, LongBowlType.LEGBREAK_GOOGLY, LongBowlType.LEFT_ARM_WRIST_SPIN, LongBowlType.SLOW_LEFT_ARM_CHINAMAN]
  const array_intersection = WRIST_SPIN_LONG_TYPES.filter(value => update.longBowlTypes?.includes(value));
  return array_intersection && array_intersection.length > 0
}

export function filterByFingerSpin(
  update: BirdBowlData
): boolean {
  const FINGER_SPIN_LONG_TYPES = [LongBowlType.RIGHT_ARM_OFFBREAK, LongBowlType.SLOW_LEFT_ARM_ORTHODOX]
  const array_intersection = FINGER_SPIN_LONG_TYPES.filter(value => update.longBowlTypes?.includes(value));
  return array_intersection && array_intersection.length > 0
}

export function filterByOtherBowlingType(
  update: BirdBowlData
): boolean {
  return ![BowlType.LEFT_ARM_PACE, BowlType.LEFT_ARM_SPIN, BowlType.RIGHT_ARM_PACE, BowlType.RIGHT_ARM_SPIN].includes(update.bowlType)
}

export function applyInningsAndOverFilters(
  updates: BirdBowlData[],
  overRange: { start: number; end: number },
  selectedInnings: "both" | "first" | "second"
) {
  return updates.filter((update) => {
    const seq = update.sequence;
    const innings = Math.floor(seq / 1000000);
    const over = Math.floor((seq % 1000000) / 1000);

    const inningsMatch =
      selectedInnings === "both" ||
      (selectedInnings === "first" && innings === 1) ||
      (selectedInnings === "second" && innings === 2);


    const overMatch = over - 1 >= overRange.start && over <= overRange.end;



    return inningsMatch && overMatch;
  });
}

export function linearRegression(data: { x: number; y: number }[]) {
  const n = data.length;
  const sumX = data.reduce((acc, val) => acc + val.x, 0);
  const sumY = data.reduce((acc, val) => acc + val.y, 0);
  const sumXY = data.reduce((acc, val) => acc + val.x * val.y, 0);
  const sumX2 = data.reduce((acc, val) => acc + val.x * val.x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}

export function getMetricValue(update: BirdBowlData, metric: string): number {
  const value = update[metric as keyof BirdBowlData] as number;
  return metric === "deviation" || metric === "swing" ? Math.abs(value) : value;
}

export function calculateSlopeAndIntercept(
  processedDeliveries: (BirdBowlData & { processedSequence })[],
  selectedMetric: InsightsPageMetric
): [number, number] {
  const lr = linearRegression(
    processedDeliveries.map((update) => {
      return {
        x: update.processedSequence,
        y: getMetricValue(update, selectedMetric),
      };
    })
  );
  return [lr.slope, lr.intercept];
}

export function getLinePointsForInnings(
  processedDeliveries: ReadonlyArray<(BirdBowlData & { processedSequence })>,
  innings: number,
  selectedMetric: InsightsPageMetric
): { x: number; y: number }[] {
  const thisInningsDeliveries = processedDeliveries.filter(
    (update) => Number(update.sequence.toString()[0]) === innings
  );

  const [slope, intercept] = calculateSlopeAndIntercept(
    thisInningsDeliveries,
    selectedMetric
  );

  const xCoords: number[] = thisInningsDeliveries.map(
    (update) => update.processedSequence
  );

  return xCoords.map((x) => ({
    x,
    y: slope * x + intercept,
  }));
}

export function getExpectedRatios(
  updates: BirdBowlData[],
  type: "strikeRate" | "wicketPercent"
): { [key: number]: number[] } {
  const groupedByYear: { [key: number]: number[] } = {};

  const updatesByYearAndMatch: {
    [year: number]: { [matchId: string]: BirdBowlData[] };
  } = {};

  updates.forEach((update) => {
    if (type === "strikeRate" && (update.wide || update.noBall)) return;

    if (!updatesByYearAndMatch[update.year]) {
      updatesByYearAndMatch[update.year] = {};
    }
    if (!updatesByYearAndMatch[update.year][update.birdMatchId]) {
      updatesByYearAndMatch[update.year][update.birdMatchId] = [];
    }
    updatesByYearAndMatch[update.year][update.birdMatchId].push(update);
  });

  for (const year in updatesByYearAndMatch) {
    const matchData = updatesByYearAndMatch[year];
    groupedByYear[Number(year)] = [];

    for (const matchId in matchData) {
      const deliveries = matchData[matchId];

      if (type === "strikeRate") {
        const validExpectations = deliveries
          .map((u) => u.expectation?.strikeRate)
          .filter((val): val is number => val !== undefined);
        const runs = deliveries.map((u) => u.runs || 0);

        const totalExpected = validExpectations.reduce((a, b) => a + b, 0);
        const totalRuns = runs.reduce((a, b) => a + b, 0);

        if (totalExpected > 0) {
          groupedByYear[Number(year)].push(totalRuns / totalExpected);
        }
      } else {
        const validExpectations = deliveries
          .map((u) => u.expectation?.wicketPercent)
          .filter((val): val is number => val !== undefined);
        const wickets = deliveries.filter((u) => u.dismissal).length;

        const totalExpected = validExpectations.reduce((a, b) => a + b, 0);

        if (totalExpected > 0) {
          groupedByYear[Number(year)].push(wickets / totalExpected);
        }
      }
    }
  }

  return groupedByYear;
}

export function combineGroupedHistograms(
  grouped: { [key: number]: number[] },
  labelBase: string,
  colorPalette: string[]
): { labels: string[]; datasets: any[] } {
  const allValues = Object.values(grouped).flat();
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const binSize = (maxVal - minVal) / BIN_COUNT || 1;

  const labels = Array.from({ length: BIN_COUNT }, (_, i) => {
    const start = (minVal + i * binSize).toFixed(2);
    const end = (minVal + (i + 1) * binSize).toFixed(2);
    return `${start}-${end}`;
  });

  const datasets = Object.entries(grouped).map(([year, values], index) => {
    const bins = new Array(BIN_COUNT).fill(0);
    values.forEach((value) => {
      const binIndex = Math.min(
        Math.floor((value - minVal) / binSize),
        BIN_COUNT - 1
      );
      bins[binIndex]++;
    });

    const total = bins.reduce((sum, val) => sum + val, 0) || 1;
    const normalisedBins = bins.map((count) => count / total);

    return {
      label: `${labelBase} ${year}`,
      data: normalisedBins,
      backgroundColor: colorPalette[index % colorPalette.length],
      barThickness: 8,
      barPercentage: 1.0,
      categoryPercentage: 1.0,
      stack: "overlap",
    };
  });

  return { labels, datasets };
}
